import { useCallback } from 'react';

import getMonth from 'date-fns/getMonth';
import getYear from 'date-fns/getYear';

import {
	getLocalStorageItemAsString,
	setLocalStorageItem,
} from '@atlassian/search-common/local-storage';

const COOLDOWN_STORAGE_KEY = 'quick_find_connect_apps_cooldown';
const MAX_ANIMATIONS_PER_MONTH = 10;
const DISMISSED_VALUE = 'dismissed';

type CooldownStore = {
	count: number;
	monthKey: string;
};

const getCurrentMonthKey = (): string => {
	const now = new Date();
	return `${getYear(now)}_m${getMonth(now)}`;
};

const readStore = (): CooldownStore | null => {
	const raw = getLocalStorageItemAsString(COOLDOWN_STORAGE_KEY);
	if (!raw) {
		return null;
	}
	try {
		const parsed = JSON.parse(raw) as unknown;
		if (
			parsed &&
			typeof parsed === 'object' &&
			'count' in parsed &&
			'monthKey' in parsed &&
			typeof (parsed as CooldownStore).count === 'number' &&
			typeof (parsed as CooldownStore).monthKey === 'string'
		) {
			return parsed as CooldownStore;
		}
		return null;
	} catch {
		// JSON parse error — degrade gracefully.
		return null;
	}
};

const isDismissed = (): boolean => {
	return getLocalStorageItemAsString(COOLDOWN_STORAGE_KEY) === DISMISSED_VALUE;
};

const writeStore = (store: CooldownStore): void => {
	try {
		setLocalStorageItem(COOLDOWN_STORAGE_KEY, JSON.stringify(store));
	} catch {
		// Private browsing / storage full / quota exceeded — degrade gracefully
		// (the animation plays anyway; we just lose the count for this attempt).
	}
};

/** Per-browser frequency cap for the Connect Apps animation. Resets on month rollover. */
export const useConnectAppsCooldown = (
	maxPerMonth: number = MAX_ANIMATIONS_PER_MONTH,
): { canAnimate: () => boolean; recordAnimation: () => void; dismissPermanently: () => void } => {
	const canAnimate = useCallback((): boolean => {
		if (isDismissed()) {
			return false;
		}
		const monthKey = getCurrentMonthKey();
		const stored = readStore();
		if (stored?.monthKey === monthKey && stored.count >= maxPerMonth) {
			return false;
		}
		return true;
	}, [maxPerMonth]);

	const recordAnimation = useCallback((): void => {
		const monthKey = getCurrentMonthKey();
		const stored = readStore();
		const count = stored?.monthKey === monthKey ? stored.count + 1 : 1;
		writeStore({ monthKey, count });
	}, []);

	const dismissPermanently = useCallback((): void => {
		try {
			setLocalStorageItem(COOLDOWN_STORAGE_KEY, DISMISSED_VALUE);
		} catch {
			// degrade gracefully
		}
	}, []);

	return { canAnimate, recordAnimation, dismissPermanently };
};
